import {
  Flex,
  FlexItem,
  Title,
  TextContent,
  TextList,
  TextListVariants,
  TextListItem,
  TextListItemVariants,
  Popover,
  Button,
} from '@patternfly/react-core'
import {
  CheckCircleIcon,
  TimesCircleIcon,
  InfoCircleIcon,
} from '@patternfly/react-icons'
import * as React from 'react'
import { useState, useCallback } from 'react'

import { UpdatePatientStatus } from '@app/components/patient/patient-card/patient-status'
import { useFeatureFlags } from '@app/domain/feature-flags/feature-flags.hooks'
import { FeatureFlagId } from '@app/domain/feature-flags/feature-flags.interface'
import {
  getPhoneTypeLabel,
  emailAddressFromPatient,
  phoneNumberFromPatient,
  phoneNumberTypePatient,
} from '@app/domain/patient/patient.helpers'
import type {
  IPatient,
  IPatientCommunicationConsent,
} from '@app/domain/patient/patient.interface'
import type * as Patients from '@app/state/patients'
import { SmallButton } from '@app/utils/styles/small-button'

interface ConsentIndicatorProps {
  consented: boolean
  children: React.ReactNode
}

export const ConsentIndicator = ({
  consented,
  children,
}: ConsentIndicatorProps) => {
  {
    return consented ? (
      <span style={{ color: 'green' }}>
        <CheckCircleIcon /> {children}
      </span>
    ) : (
      <span style={{ color: 'red' }}>
        {' '}
        <TimesCircleIcon /> {children}
      </span>
    )
  }
}

interface Props {
  onStartEdit: () => void
  patient: IPatient
  updateStatus?: Patients.PatientActionStatusId
  communicationConsent?: IPatientCommunicationConsent | null
  onUpdateEmailConsent: (
    patientId: string,
    consentsToEmail: boolean,
  ) => Promise<void>
  onRemoveSmsConsent: (patientId: string) => Promise<void>
  incomingSmsNumber?: string
}

export const ReadOnlyContactCard = ({
  onStartEdit,
  patient,
  updateStatus,
  communicationConsent,
  onUpdateEmailConsent,
  onRemoveSmsConsent,
  incomingSmsNumber,
}: Props) => {
  const featureFlags = useFeatureFlags()
  const emailConsented = communicationConsent?.consentsToEmail ?? false
  const smsConsented = communicationConsent?.consentsToSms ?? false

  const hasEmail = !!emailAddressFromPatient(patient.contactInformation)
  const hasPhone = !!phoneNumberFromPatient(patient.contactInformation)

  const [emailPopoverVisible, setEmailPopoverVisible] = useState(false)
  const [smsPopoverVisible, setSmsPopoverVisible] = useState(false)

  const handleConsentEmail = useCallback(() => {
    onUpdateEmailConsent(patient.id, true)
    setEmailPopoverVisible(false)
  }, [patient.id, onUpdateEmailConsent])

  const handleRemoveConsentEmail = useCallback(() => {
    onUpdateEmailConsent(patient.id, false)
    setEmailPopoverVisible(false)
  }, [patient.id, onUpdateEmailConsent])

  const handleRemoveConsentSms = useCallback(() => {
    onRemoveSmsConsent(patient.id)
    setSmsPopoverVisible(false)
  }, [patient.id, onRemoveSmsConsent])

  const toggleEmailPopover = useCallback(() => {
    setEmailPopoverVisible((prev) => !prev)
  }, [])

  const toggleSmsPopover = useCallback(() => {
    setSmsPopoverVisible((prev) => !prev)
  }, [])

  const emailPopoverContent = (
    <div>
      <p>
        {emailConsented
          ? 'Patient has provided consent to be contacted by email.'
          : 'Patient has removed consent to be contacted by email.'}
      </p>
      <div
        style={{
          marginTop: '1rem',
          display: 'flex',
          gap: '0.5rem',
        }}
      >
        {emailConsented ? (
          <SmallButton variant="danger" onClick={handleRemoveConsentEmail}>
            Remove Consent
          </SmallButton>
        ) : (
          <SmallButton variant="primary" onClick={handleConsentEmail}>
            Reinstate Consent
          </SmallButton>
        )}
      </div>
    </div>
  )

  const smsPopoverContent = (
    <div>
      {smsConsented ? (
        <p>Patient has provided consent to be contacted by SMS.</p>
      ) : (
        <p>
          Patient has removed consent to be contacted by SMS.
          {incomingSmsNumber && (
            <>
              {' '}
              To re-consent, <br />
              ask the patient to text <strong>START</strong> to{' '}
              {incomingSmsNumber}.
            </>
          )}
        </p>
      )}
      {smsConsented && (
        <div
          style={{
            marginTop: '1rem',
            display: 'flex',
          }}
        >
          <SmallButton variant="danger" onClick={handleRemoveConsentSms}>
            Remove Consent
          </SmallButton>
        </div>
      )}
    </div>
  )

  return (
    <Flex direction={{ default: 'column' }}>
      <FlexItem>
        <Flex direction={{ default: 'row' }}>
          <FlexItem>
            <Title headingLevel="h2" size="lg">
              Contact Information
            </Title>
          </FlexItem>
          <FlexItem>
            <SmallButton variant="secondary" onClick={onStartEdit}>
              Edit
            </SmallButton>
          </FlexItem>
          <FlexItem>
            <UpdatePatientStatus updateStatus={updateStatus} />
          </FlexItem>
        </Flex>
      </FlexItem>
      <FlexItem>
        <TextContent>
          <TextList component={TextListVariants.dl}>
            <TextListItem component={TextListItemVariants.dt}>
              Email Address:
            </TextListItem>
            <TextListItem component={TextListItemVariants.dd}>
              <Flex
                spaceItems={{ default: 'spaceItemsSm' }}
                alignItems={{ default: 'alignItemsCenter' }}
              >
                <FlexItem>
                  {emailAddressFromPatient(patient.contactInformation)}
                </FlexItem>
                {hasEmail &&
                featureFlags.isFeatureFlagEnabled(
                  FeatureFlagId.RemoteInteraction,
                ) ? (
                  <FlexItem>
                    <ConsentIndicator consented={emailConsented}>
                      Communication Consent
                    </ConsentIndicator>

                    <Popover
                      aria-label="Email consent information"
                      bodyContent={emailPopoverContent}
                      isVisible={emailPopoverVisible}
                      hasAutoWidth
                      shouldClose={() => setEmailPopoverVisible(false)}
                    >
                      <Button
                        variant="plain"
                        aria-label="Email consent info"
                        onClick={toggleEmailPopover}
                        style={{ marginLeft: '0.5rem', padding: '0' }}
                      >
                        <InfoCircleIcon />
                      </Button>
                    </Popover>
                  </FlexItem>
                ) : null}
              </Flex>
            </TextListItem>
            <TextListItem component={TextListItemVariants.dt}>
              Phone Number:
            </TextListItem>
            <TextListItem component={TextListItemVariants.dd}>
              <Flex
                spaceItems={{ default: 'spaceItemsSm' }}
                alignItems={{ default: 'alignItemsCenter' }}
              >
                <FlexItem>
                  {phoneNumberFromPatient(patient.contactInformation)}
                </FlexItem>
                {hasPhone &&
                featureFlags.isFeatureFlagEnabled(
                  FeatureFlagId.RemoteInteraction,
                ) ? (
                  <FlexItem>
                    <ConsentIndicator consented={smsConsented}>
                      Communication Consent
                    </ConsentIndicator>
                    <Popover
                      aria-label="SMS consent information"
                      bodyContent={smsPopoverContent}
                      hasAutoWidth
                      isVisible={smsPopoverVisible}
                      shouldClose={() => setSmsPopoverVisible(false)}
                    >
                      <Button
                        variant="plain"
                        aria-label="SMS consent info"
                        onClick={toggleSmsPopover}
                        style={{ marginLeft: '0.5rem', padding: '0' }}
                      >
                        <InfoCircleIcon />
                      </Button>
                    </Popover>
                  </FlexItem>
                ) : null}
              </Flex>
            </TextListItem>
            <TextListItem component={TextListItemVariants.dt}>
              Phone Type:
            </TextListItem>
            <TextListItem component={TextListItemVariants.dd}>
              {getPhoneTypeLabel(
                phoneNumberTypePatient(patient.contactInformation),
              )}
            </TextListItem>
          </TextList>
        </TextContent>
      </FlexItem>
    </Flex>
  )
}
